import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MOVE_STRINGS, TierKey } from '../../constants';

import { Fireworks } from 'modules/daily-quests-page/components/fireworks/fireworks';

import ModalWithFullScreenBg from 'shared/components/ui/Modal/ModalWithFullScreenBg';
import { ButtonWithLoader } from 'shared/components/ui/PrimaryButton/button-with-loader';
import SecondaryBtn from 'shared/components/ui/SecondaryBtn/SecondaryBtn';
import { ImageData, OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';
import { MovesEnum } from 'shared/constants/shared-enums';
import { useAccount, usePublicClient, useWalletClient } from 'shared/hooks/use-web3';
import { useQuestsStore } from 'shared/store/questsStore';

import closeX from 'assets/svg/close-x.svg';
import ForgeIcon from 'assets/svg/forge.svg?react';

import { CardsBlock } from './cards-block';

interface IForgeModal {
  open: boolean;
  onClose: () => void;
  cardToForge: ImageData;
  cardFromForge: ImageData;
  max: number;
  tierId: number;
  move: MovesEnum;
  collection: TierKey;
  prevCollection: TierKey;
}

export const ForgeModal: FC<IForgeModal> = ({
  open,
  onClose,
  cardToForge,
  cardFromForge,
  tierId,
  max,
  move,
  collection,
  prevCollection,
}) => {
  const { t } = useTranslation();
  const { forgeNft, isForging, isForgeSuccess, forgeError } = useQuestsStore();
  const { address } = useAccount();
  const publicClient = usePublicClient();
  const walletClient = useWalletClient();
  const [count, setCount] = useState<number>(max);
  const [loaderFinished, setLoaderFinished] = useState<boolean>(false);
  const [showFireworks, setShowFireworks] = useState<boolean>(false);
  const tRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setCount(max);
  }, [max]);

  const percent = useMemo(() => Math.min(count * 10, 100), [count]);
  const canForge = count > 0;

  const inc = useCallback(() => setCount((c) => Math.min(c + 1, max)), [max]);
  const dec = useCallback(() => setCount((c) => Math.max(c - 1, 0)), []);

  const handleForgeClick = useCallback(async () => {
    if (
      !publicClient ||
      !walletClient ||
      !address ||
      !tierId ||
      !count ||
      !move ||
      !collection ||
      !prevCollection
    )
      return;
    await forgeNft(
      publicClient,
      walletClient,
      address as `0x${string}`,
      tierId,
      count,
      move,
      collection,
      prevCollection,
    );
  }, [
    forgeNft,
    publicClient,
    walletClient,
    address,
    tierId,
    count,
    move,
    collection,
    prevCollection,
  ]);

  const onEndAnimation = useCallback(async () => {
    setShowFireworks(true);
    setLoaderFinished(true);
  }, []);

  useEffect(() => {
    if (showFireworks) {
      tRef.current = setTimeout(() => {
        setShowFireworks(false);
      }, 1500);
    }
  }, [showFireworks]);

  useEffect(() => {
    return () => {
      if (tRef.current) clearTimeout(tRef.current);
    };
  }, []);

  const wrapperClass = useMemo(() => {
    if (isForgeSuccess && loaderFinished) {
      return 'rounded-3xl bg-[linear-gradient(180deg,_#080540_0%,_#52382A_100%)] pt-[1.125rem]';
    }
    if (!isForgeSuccess && loaderFinished) {
      return 'rounded-3xl bg-[linear-gradient(180deg,_#080540_0%,_#7A3556_100%)] pt-[1.125rem]';
    }
    return 'rounded-3xl bg-[linear-gradient(180deg,_#080540_0%,_#030220_100%)] pt-[1.125rem]';
  }, [isForgeSuccess, loaderFinished]);

  const heading = useMemo(() => {
    if (forgeError) {
      return { text: 'FORGE ERROR', className: 'from-[#FFEAE0] to-[#FFBE9E]' };
    }
    if (isForgeSuccess && loaderFinished)
      return { text: 'SUCCESS', className: 'from-[#FFF7E0] to-[#FFE79D]' };
    if (!isForgeSuccess && loaderFinished)
      return { text: 'FAIL', className: 'from-[#FFE1E4] to-[#FFA1AC]' };
    return { text: 'FORGE', className: 'from-white to-[#B5E0FF]' };
  }, [isForgeSuccess, loaderFinished, forgeError]);

  const content = useMemo(() => {
    if (isForgeSuccess && loaderFinished && !forgeError) {
      return (
        <div className="relative flex h-full max-h-[10rem] w-full flex-col items-center justify-center rounded-xl border border-indigo bg-[#05055B] text-xl">
          <span className="bg-gradient-to-b from-white to-[#B5E0FF] bg-clip-text text-center text-xl font-extrabold text-transparent">
            {t('nft.youveForged')}
          </span>
          <span className="bg-gradient-to-b from-[#E9F8FF] to-[#94DBFF] bg-clip-text text-center text-xl font-extrabold capitalize text-transparent">
            {collection} {MOVE_STRINGS[move]}
          </span>
        </div>
      );
    }

    if (!isForgeSuccess && loaderFinished && !forgeError) {
      return (
        <div className="flex h-full max-h-[10rem] w-full flex-col items-center justify-center rounded-xl border border-indigo bg-[#05055B] text-xl">
          <span className="bg-gradient-to-b from-white to-[#B5E0FF] bg-clip-text text-center text-xl font-extrabold text-transparent">
            {t('nft.youveLostOne')}
          </span>
          <span className="bg-gradient-to-b from-[#FFF1EA] to-[#FFB895] bg-clip-text text-center text-xl font-extrabold capitalize text-transparent">
            {prevCollection} {MOVE_STRINGS[move]}
          </span>
        </div>
      );
    }

    return (
      <CardsBlock
        count={count}
        max={max}
        cardToMint={cardFromForge}
        dec={dec}
        inc={inc}
        disabled={isForging}
      />
    );
  }, [isForgeSuccess, count, max, cardToForge, tierId, move, collection, loaderFinished]);

  const closeHandler = useCallback(() => {
    onClose();
    setLoaderFinished(false);
  }, [onClose]);

  return (
    <ModalWithFullScreenBg
      open={open}
      className="bg-background/60"
      withGridBackground={false}
      childrenClassName={'border-[2px] border-border sm:border-[3px] max-w-[25rem]'}
    >
      <div className={wrapperClass}>
        <h2
          className={`bg-gradient-to-b bg-clip-text text-center text-xl font-extrabold text-transparent ${heading.className}`}
        >
          {heading.text}
        </h2>

        <button onClick={closeHandler} className="absolute right-4 top-4">
          <img src={closeX} alt="Close" className="h-6 w-6" />
        </button>

        {/* Content */}
        <div className="mb-4 grid grid-cols-[6.625rem,1fr] gap-2 p-4">
          <div className="relative">
            <OptimizedImage
              imageData={!isForgeSuccess ? cardFromForge : cardToForge}
              alt="Result card"
              className="h-[161px] w-[106px]"
            />
            {showFireworks && isForgeSuccess && <Fireworks />}
            {isForgeSuccess && (
              <span
                className={`absolute bottom-[1rem] right-[-0.5rem] flex h-9 w-9 items-center justify-center rounded-full border-[3px] text-[1rem] font-semibold ${
                  isForgeSuccess
                    ? 'border-[#C0820B] bg-[linear-gradient(180deg,_#FFC000_0%,_#FF8B00_100%)] text-[#6A3B09]'
                    : 'border-[#9C0D0D] bg-[linear-gradient(180deg,_#FF5959_0%,_#FF0000_100%)] text-[#FFFFFF]'
                } `}
              >
                {isForgeSuccess ? '+1' : '-1'}
              </span>
            )}
          </div>
          {content}
        </div>

        {/* Bottom bar with Forge button */}
        <div className="w-full rounded-b-3xl border-t-4 border-border bg-[linear-gradient(180deg,_#060771_0%,_#080542_100%)] px-4 pb-4 pt-3">
          <div className="relative">
            {!loaderFinished ? (
              <ButtonWithLoader
                text={`Forge (${percent}% Chance)`}
                svg={<ForgeIcon />}
                onClick={handleForgeClick}
                disabled={!canForge}
                className="relative w-full"
                onEnd={onEndAnimation}
                isLoading={isForging}
              />
            ) : (
              <SecondaryBtn
                text={t('nft.backToCollection')}
                onClick={closeHandler}
                className="relative w-full"
              />
            )}
          </div>
        </div>
      </div>
    </ModalWithFullScreenBg>
  );
};
